import { Fragment, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';

import { obtenerBalance, limpiarBalance } from '../../../actions/pagosVentas';

import NuevoPagoModal from './modals/NuevoPagoModal';
import EditarPagoModal from './modals/EditarPagoModal';
import EliminarPagoModal from './modals/EliminarPagoModal';

const formatDate = (value) => {
    if (!value) return 'Sin definir';
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString('es-MX');
};

const formatMoney = (n) => {
    const v = Number(n || 0);
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(v);
};

const monedaTextoPago = (pago) => (Number(pago?.moneda) === 1 ? 'USD' : 'MXN');

const paginationOptions = {
    rowsPerPageText: 'Filas por página',
    rangeSeparatorText: 'de',
    selectAllRowsItem: true,
    selectAllRowsItemText: 'Todos'
};

const PagosCliente = ({ idCliente }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { pagos, loadingBalance } = useSelector(state => state.pagosVentas);

    const [textoFiltro, setTextoFiltro] = useState('');
    const [modalNuevo, setModalNuevo] = useState(false);
    const [pagoEditar, setPagoEditar] = useState(null);
    const [pagoEliminar, setPagoEliminar] = useState(null);

    useEffect(() => {
        if (!idCliente) return;

        dispatch(obtenerBalance(idCliente));

        return () => {
            dispatch(limpiarBalance());
        };
    }, [dispatch, idCliente]);

    const pagosFiltrados = useMemo(() => {
        const q = (textoFiltro || '').trim().toLowerCase();
        if (!q) return pagos || [];

        return (pagos || []).filter(p => {
            const metodo = (p?.metodo_pago?.nombre || '').toLowerCase();
            const referencia = (p?.referencia || '').toLowerCase();
            const observaciones = (p?.observaciones || '').toLowerCase();
            const responsable = `${p?.responsable?.nombre || ''} ${p?.responsable?.apellido || ''}`.toLowerCase();
            const venta = (p?.venta?.folio || '').toLowerCase();
            const moneda = monedaTextoPago(p).toLowerCase();
            return metodo.includes(q) || referencia.includes(q) || observaciones.includes(q) || responsable.includes(q) || venta.includes(q) || moneda.includes(q);
        });
    }, [pagos, textoFiltro]);

    const columns = useMemo(() => ([
        {
            /* La fecha se normaliza en el backend a una marca horaria fija por día, así que dos
               pagos del mismo día quedan con el mismo timestamp. Se desempata con el _id de Mongo
               (creciente con el orden real de creación) para que el más reciente siempre quede primero. */
            name: 'Fecha',
            selector: row => `${String(row?.fecha ? new Date(row.fecha).getTime() : 0).padStart(15, '0')}${row?._id || ''}`,
            sortable: true,
            wrap: true,
            grow: 1,
            cell: row => formatDate(row?.fecha)
        },
        {
            name: 'Monto',
            selector: row => row?.monto || 0,
            sortable: true,
            right: true,
            cell: row => <div style={{ width: '100%', textAlign: 'right' }}>$ {formatMoney(row?.monto)}</div>,
            grow: 1
        },
        {
            name: 'Moneda',
            selector: row => monedaTextoPago(row),
            sortable: true,
            center: true,
            cell: row => (
                <span className={`badge ${Number(row?.moneda) === 1 ? 'bg-info text-dark' : 'bg-light text-dark border'}`}>
                    {monedaTextoPago(row)}
                </span>
            ),
            grow: 1
        },
        {
            name: 'Venta',
            selector: row => row?.venta?.folio || 'Abono general',
            sortable: true,
            wrap: true,
            grow: 1,
            cell: row => (
                row?.venta?._id
                    ? (
                        <button
                            type="button"
                            className="btn btn-sm btn-link p-0"
                            onClick={() => navigate(`/ventas/editar/${row.venta._id}`)}
                        >
                            {row.venta.folio}
                        </button>
                    )
                    : <span className="text-muted">Abono general</span>
            )
        },
        {
            name: 'Método de pago',
            selector: row => row?.metodo_pago?.nombre || 'Sin definir',
            sortable: true,
            wrap: true,
            grow: 1
        },
        {
            name: 'Referencia',
            selector: row => row?.referencia || '—',
            sortable: true,
            wrap: true,
            grow: 1
        },
        {
            name: 'Responsable',
            selector: row => `${row?.responsable?.nombre || ''} ${row?.responsable?.apellido || ''}`.trim() || 'Sin definir',
            sortable: true,
            wrap: true,
            grow: 1
        },
        {
            name: 'Opciones',
            width: '120px',
            cell: row => (
                <div className="d-flex align-items-center gap-2">
                    <button
                        type="button"
                        className="btn btn-sm btn-link px-2"
                        title="Editar"
                        onClick={() => setPagoEditar(row)}
                    >
                        <i className="fa fa-edit text-warning" />
                    </button>

                    <button
                        type="button"
                        className="btn btn-sm btn-link px-2"
                        title="Eliminar"
                        onClick={() => setPagoEliminar(row)}
                    >
                        <i className="fas fa-trash text-danger" />
                    </button>
                </div>
            )
        }
    ]), [navigate]);

    return (
        <Fragment>
            <div className="d-flex justify-content-end mb-3">
                <div className="d-flex align-items-center gap-2 w-100 justify-content-end">
                    <div style={{ minWidth: 280 }}>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Buscar en la tabla..."
                            value={textoFiltro}
                            onChange={(e) => setTextoFiltro(e.target.value)}
                        />
                    </div>

                    <button type="button" className="btn btn-primary" onClick={() => setModalNuevo(true)}>
                        <i className="feather icon-plus" /> Registrar pago
                    </button>
                </div>
            </div>

            <div className="mt-2">
                <DataTable
                    columns={columns}
                    data={pagosFiltrados}
                    progressPending={loadingBalance && (!pagos || pagos.length === 0)}
                    defaultSortFieldId={1}
                    defaultSortAsc={false}
                    pagination
                    paginationComponentOptions={paginationOptions}
                    highlightOnHover
                    striped
                    responsive
                    persistTableHead
                    noDataComponent="Sin pagos registrados"
                />
            </div>

            <NuevoPagoModal
                show={modalNuevo}
                idCliente={idCliente}
                onHide={() => setModalNuevo(false)}
                onSuccess={() => setModalNuevo(false)}
            />

            <EditarPagoModal
                show={!!pagoEditar}
                idCliente={idCliente}
                pago={pagoEditar}
                onHide={() => setPagoEditar(null)}
                onSuccess={() => setPagoEditar(null)}
            />

            <EliminarPagoModal
                show={!!pagoEliminar}
                idCliente={idCliente}
                idPago={pagoEliminar?._id || null}
                onHide={() => setPagoEliminar(null)}
            />
        </Fragment>
    );
};

export default PagosCliente;
